/**
 * Lightweight-charts custom series: filled MA Ribbon (EMA fast/slow) + boundary lines.
 * Matches TradingView-style green (fast > slow) / red (fast < slow) cloud.
 */

const DEFAULT_OPTIONS = {
  bullFill: 'rgba(38, 166, 154, 0.28)',
  bearFill: 'rgba(239, 83, 80, 0.28)',
  bullLine: 'rgba(38, 166, 154, 0.95)',
  bearLine: 'rgba(239, 83, 80, 0.95)',
  lineWidth: 2,
};

class MaRibbonPaneRenderer {
  constructor() {
    this._data = null;
    this._options = DEFAULT_OPTIONS;
  }

  draw(target, priceConverter) {
    target.useBitmapCoordinateSpace((scope) => {
      this._draw(scope, priceConverter);
    });
  }

  update(data, options) {
    this._data = data;
    this._options = { ...DEFAULT_OPTIONS, ...options };
  }

  _draw(scope, priceConverter) {
    const data = this._data;
    const options = this._options;
    if (!data?.bars?.length || data.visibleRange == null) return;

    const { context, horizontalPixelRatio, verticalPixelRatio } = scope;
    const bars = data.bars;
    const from = data.visibleRange.from;
    const to = data.visibleRange.to;
    const halfBar =
      (data.barSpacing / 2) * horizontalPixelRatio;

    const yAt = (price) => {
      const y = priceConverter(price);
      if (y == null || Number.isNaN(y)) return null;
      return y * verticalPixelRatio;
    };

    // Filled ribbon per bar
    for (let i = from; i < to; i++) {
      const bar = bars[i];
      const row = bar?.originalData;
      if (!row || row.fast == null || row.slow == null) continue;

      const yFast = yAt(row.fast);
      const ySlow = yAt(row.slow);
      if (yFast == null || ySlow == null) continue;

      const bullish = row.fast >= row.slow;
      context.fillStyle = bullish ? options.bullFill : options.bearFill;

      const top = Math.min(yFast, ySlow);
      const bottom = Math.max(yFast, ySlow);
      const x = bar.x * horizontalPixelRatio;
      const w = Math.max(halfBar * 2, 1);
      context.fillRect(x - halfBar, top, w, bottom - top);
    }

    // Boundary lines (fast + slow)
    const drawLine = (key) => {
      context.lineWidth = options.lineWidth * horizontalPixelRatio;
      let started = false;
      let prevBull = null;

      for (let i = from; i < to; i++) {
        const bar = bars[i];
        const row = bar?.originalData;
        if (!row || row[key] == null || row.fast == null || row.slow == null) {
          started = false;
          prevBull = null;
          continue;
        }

        const y = yAt(row[key]);
        if (y == null) {
          started = false;
          prevBull = null;
          continue;
        }

        const bullish = row.fast >= row.slow;
        const x = bar.x * horizontalPixelRatio;

        if (!started || prevBull !== bullish) {
          if (started) context.stroke();
          context.beginPath();
          context.strokeStyle = bullish ? options.bullLine : options.bearLine;
          context.moveTo(x, y);
          started = true;
          prevBull = bullish;
        } else {
          context.lineTo(x, y);
        }
      }
      if (started) context.stroke();
    };

    drawLine('fast');
    drawLine('slow');
  }
}

export class MaRibbonPaneView {
  constructor() {
    this._renderer = new MaRibbonPaneRenderer();
  }

  renderer() {
    return this._renderer;
  }

  update(data, options) {
    this._renderer.update(data, options);
  }

  priceValueBuilder(plotRow) {
    if (!plotRow || plotRow.fast == null || plotRow.slow == null) {
      return [NaN];
    }
    return [Math.min(plotRow.fast, plotRow.slow), Math.max(plotRow.fast, plotRow.slow), plotRow.fast];
  }

  isWhitespace(data) {
    return !data || data.fast == null || data.slow == null;
  }

  defaultOptions() {
    return { ...DEFAULT_OPTIONS };
  }
}
